import { useState, createContext, ReactNode, useEffect, useMemo, JSX } from 'react';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';

export interface WtoVariant {
  variant_id: string;
  variant_url: string;
}

export interface WtoVariantSplit {
  split: number;
  variant_id: string;
}

export interface WtoDowVariant {
  name: string;
  url: string;
  split: number;
}

export interface WtoDowOperation {
  day: number[];
  start_time: string;
  end_time: string;
  formattedStartTime: string;
  formattedEndTime: string;
  time_zone: string;
  variants: WtoDowVariant[];
}

export interface WtoCondition {
  operator: string;
  trigger: string;
  rule: string;
  value: string;
}

export interface WtoUtmUrl {
  utm_variant_id: string;
  utm_variant_url: string | string[];
}

export interface WtoUtmOperation {
  operation: string;
  utm_key: string;
  utm_value: string;
  replace_key?: string;
  replace_value?: string;
}

export interface WtoAdditionalUtm {
  additional_utm_url: WtoUtmUrl[];
  additional_utm_operation: WtoUtmOperation[];
}

export interface WtoCookieOperation {
  operation: string;
  cookie_key: string;
  cookie_value: string;
  cki_replace_key?: string;
  cki_replace_value?: string;
}

export interface WtoCookieOperationUrl {
  cookie_variant_id: string;
  cookie_variant_url?: string | string[];
}

export interface WtoCustomRetention {
  customRetentionStatus?: boolean;
  formattedStartDate?: string;
  startDate?: string;
  formattedStartTime?: string;
  startTime?: string;
  time_zone?: string;
}

export interface WtoTimeframe {
  startDateTime: string;
  formattedStartDateTime: string;
  endDateTime: string;
  formattedEndDateTime: string;
  time_zone: string;
  timeframe_utm_test_id_enabled: boolean;
  utm_test_id: string;
}

export interface WtoRule {
  rule_name: string;
  rule_order: string;
  variant_splits: WtoVariantSplit[];
  conditions: WtoCondition[];
  timeframe_enabled: boolean;
  timeframe?: WtoTimeframe;
  dow_operation: WtoDowOperation[];
  additional_utm: WtoAdditionalUtm[];
  cookie_operation: WtoCookieOperation[];
  cookie_operation_url: WtoCookieOperationUrl[];
  customRetention: WtoCustomRetention;
}

export const formObjDefault: Plover = {
  company: '',
  test_name: '',
  utm_test_id: '',
  default_url: '',
  default_id: '',
  plover_url: '',
  folder_path: '/',
  notes: '',
  variants: [{ variant_id: '', variant_url: '' }],
  domain: '',
  created_by: '',
  updated_by: '',
  rules: [],
  status: 'draft',
  created_at: new Date(),
  updated_at: new Date(),
  is_deleted: false
};

export const defaultRule: WtoRule = {
  additional_utm: [],
  conditions: [],
  cookie_operation: [],
  cookie_operation_url: [],
  customRetention: {},
  dow_operation: [],
  rule_name: '',
  rule_order: '0',
  timeframe_enabled: false,
  variant_splits: [
    {
      split: 0,
      variant_id: ''
    }
  ]
};

export const defaultCustomRetention: Required<WtoCustomRetention> = {
  customRetentionStatus: true,
  formattedStartDate: '',
  startDate: '',
  formattedStartTime: '',
  startTime: '',
  time_zone: 'UTC'
};

export const defaultDowRow: WtoDowOperation = {
  day: [],
  end_time: '',
  formattedEndTime: '',
  formattedStartTime: '',
  start_time: '',
  variants: [],
  time_zone: 'UTC'
};

export const defaultTimeframe: WtoTimeframe = {
  endDateTime: '',
  formattedEndDateTime: '',
  formattedStartDateTime: '',
  startDateTime: '',
  time_zone: 'UTC',
  timeframe_utm_test_id_enabled: false,
  utm_test_id: ''
};

export const FormContext = createContext<any>([[], () => {}]);

interface WtoFormContextProps {
  children: ReactNode;
}

export const WtoFormProvider = ({ children }: WtoFormContextProps): JSX.Element => {
  const [wtoFormObj, setWtoFormObj] = useState(formObjDefault);
  const [configurations, setConfigurations] = useState([]);
  const [newRule, setNewRule] = useState(defaultRule);
  useEffect(() => {}, [configurations]);

  const value = useMemo(
    () => ({ wtoFormObj, setWtoFormObj, configurations, setConfigurations, newRule, setNewRule }),
    [wtoFormObj, configurations, newRule]
  );

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
